import React, { useState } from 'react';
import { FaTrash } from 'react-icons/fa';
import toast from 'react-hot-toast';
import SwitchInput from '../../../shared/SwitchInput';

import CustomTable from '../../../shared/CustomTable';
import CustomPagination from '../../../shared/CustomPagination';
import { priceListTypesTableColumns } from '../../../constants/PriceListConstants';
import { DeleteIcon, EditIcon } from '../../../utils/TableActionIcons';
import CheckPermission from '../../../hooks/CheckPermission';
import { useTranslation } from 'react-i18next';
import {
  useUpdatePriceListTypeMutation,
  useDeletePriceListTypeMutation
} from '../../../redux/api/modules/managementApi';
const ConfirmationModal = React.lazy(() => import('../../../shared/ConfirmationModal'));

const PriceItemsTypeTable = ({
  loading,
  refetch,
  itemsData,
  payloadData,
  setPayloadData,
  selectedItemData,
  setSelectedItemData,
  setOpenCreatePriceTypeModal
}) => {
  const { hasPermission } = CheckPermission();
  const [updatePriceListType] = useUpdatePriceListTypeMutation();
  const [deletePriceListType] = useDeletePriceListTypeMutation();
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const { t } = useTranslation();

  const handleToggleActive = async (rowData, value) => {
    if (!hasPermission('pricelist_types', 'update')) return;
    const payload = {
      id: rowData?.id,
      name_en: rowData?.name_en,
      name_ar: rowData?.name_ar,
      category: rowData?.category,
      is_active: value
    };
    try {
      const res = await updatePriceListType(payload).unwrap();
      if (res) {
        toast.success(res?.message || 'Updated successfully');
        if (typeof refetch === 'function') refetch();
      }
    } catch (err) {
      toast.error(err?.data?.message || err?.message || 'Failed to update');
    }
  };

  const actionsCoumn = {
    body: actionsTemplate,
    header: t('Actions')
  };
  const columnsData = [actionsCoumn, ...priceListTypesTableColumns(t)]?.map(col => {
    if (col.field === 'is_active') {
      return {
        ...col,
        body: rowData => (
          <SwitchInput
            value={rowData?.is_active}
            disabled={!hasPermission('pricelist_types', 'update')}
            handleChange={(e) => handleToggleActive(rowData, e?.value)}
          />
        )
      };
    }
    return col;
  });

  function actionsTemplate(rowData) {
    return (
      <div className='flex items-center gap-1'>
        {hasPermission('pricelist_types', 'update') && (
          <EditIcon
            onClick={() => {
              setOpenCreatePriceTypeModal(true);
              setSelectedItemData(rowData);
            }}
          />
        )}
        {hasPermission('pricelist_types', 'delete') && (
          <DeleteIcon
            onClick={() => {
              setSelectedItemData(rowData);
              setOpenConfirmationModal(true);
            }}
          />
        )}
      </div>
    );
  }

  const onPageChange = async (newPage, newRowsPerPage) => {
    setPayloadData(prev => ({
      ...prev,
      page: newPage,
      per_page: newRowsPerPage
    }));
  };

  const handleDeleteItem = async () => {
    if (!hasPermission('pricelist_types', 'delete')) return;
    if (selectedItemData) {
      setButtonLoading(true);
      try {
        const res = await deletePriceListType({ id: selectedItemData?.id }).unwrap();
        if (res) {
          toast.success(res?.message || 'Deleted successfully');
          if (typeof refetch === 'function') refetch();
          setOpenConfirmationModal(false);
          setSelectedItemData(null);
        }
      } catch (err) {
        toast.error(err?.data?.message || err?.message || 'Failed to delete');
      }
      setButtonLoading(false);
    }
  };

  return (
    <div className='w-full h-full'>
      <div>
        <CustomTable
          data={itemsData?.data || []}
          columns={columnsData}
          isLoading={loading}
          emptyMessage={t('NoPriceListItemsDataFound')}
        />
        {itemsData?.data?.length > 0 && (
          <CustomPagination
            currentPage={itemsData?.page || payloadData?.page || 1}
            rowsPerPage={itemsData?.per_page || payloadData?.per_page || 20}
            totalRecords={itemsData?.total_records || itemsData?.total || 0}
            totalPages={itemsData?.total_pages || 1}
            onPageChange={onPageChange}
          />
        )}
      </div>
      <ConfirmationModal
        visible={openConfirmationModal}
        setVisible={setOpenConfirmationModal}
        header={t('ConfirmDelete')}
        acceptLabel={t('Delete')}
        rejectLabel={t('Cancel')}
        Icon={FaTrash}
        onAccept={handleDeleteItem}
        onReject={() => setSelectedItemData(null)}
        isLoading={buttonLoading}
      />
    </div>
  );
};

export default PriceItemsTypeTable;
